import type { BaseAPIResponse } from '~/types/api';

export interface RapidResponseEscalation {
    exposure_id: string; // UUID string
    status: 'pending' | 'assigned' | 'submitted' | 'finished';
    charge_amount: number;
    escalation_notes?: string;
    assigned_to?: {
        id: number;
        first_name: string;
        last_name: string;
    };
    elapsed_time: string;
    created_at: string;
    assigned_at?: string;
    submitted_at?: string;
    finished_at?: string;
}

export interface RapidResponseEscalationWithExposure extends RapidResponseEscalation {
    exposure: {
        id: string; // UUID string
        company_name: string;
        status: string;
    };
}

export interface CreateEscalationRequest {
    exposure_id: string;
    escalation_notes?: string;
}

export interface UpdateEscalationStatusRequest {
    status: 'pending' | 'assigned' | 'submitted' | 'finished';
    assigned_to_id?: number;
    escalation_notes?: string;
}

class RapidResponseService {


    /**
     * Get all escalations for the current user
     */
    async getEscalations(personId: number): Promise<RapidResponseEscalationWithExposure[]> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<{ escalations: RapidResponseEscalationWithExposure[] }>>(
                nuxtApp.$api.routes.people.exposures.rapid_response.index(personId),
                {
                    method: 'GET',
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to fetch escalations', status);
            }

            if (!data?.data?.escalations) {
                throw new ApiError('No escalation data received', 404);
            }

            return data.data.escalations;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    }



    /**
     * Create a new escalation for an exposure
     */
    async createEscalation(personId: number, escalationData: CreateEscalationRequest): Promise<RapidResponseEscalation> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<RapidResponseEscalation>>(
                nuxtApp.$api.routes.people.exposures.rapid_response.store(personId),
                {
                    method: 'POST',
                    body: escalationData,
                }
            );

            if (status !== 201) {
                throw new ApiError(message || 'Failed to create escalation', status);
            }


            return data?.data!;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    }

    /**
     * Update escalation status (for SOC team use)
     */
    async updateEscalationStatus(personId: number, exposureId: string, updateData: UpdateEscalationStatusRequest): Promise<RapidResponseEscalation> {
        const nuxtApp = useNuxtApp();
        
        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<RapidResponseEscalation>>(
                nuxtApp.$api.routes.people.exposures.rapid_response.update(personId, exposureId),
                {
                    method: 'PATCH',
                    body: updateData,
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to update escalation', status);
            }

            return data?.data!;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    }
}

export const rapidResponseService = new RapidResponseService();
